import { useEffect, useState } from "react";
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const mese = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("it-IT", { month: "short" });
const giorno = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" });

// popup: andamento settimanale della pulizia (100 = come appena puliti) con le piogge
export function PuliziaStorico({ open, onClose, src, d }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!open || res) return;
    src.puliziaStorico().then(setRes).catch(() => setRes({ punti: [] }));
  }, [open, res, src]);
  const pts = res ? res.punti || [] : null;
  const daily = res?.granularita === "giorno";
  const xKey = daily ? "giorno" : "settimana";
  const lineKey = daily ? "pulizia_sm" : "pulizia";
  const validi = (pts || []).filter((p) => p[lineKey] != null);
  const oggiPct = validi.length ? validi[validi.length - 1][lineKey] : null;
  const recent = daily ? validi.slice(-30) : validi.slice(-4);
  const mesePct = recent.length ? Math.round(recent.reduce((a, p) => a + p[lineKey], 0) / recent.length) : oggiPct;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="cliente-font max-w-lg rounded-3xl bg-white text-slate-800" data-testid="pulizia-storico-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-extrabold">Pulizia dei pannelli nell'ultimo anno</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">100% = pannelli come appena puliti. Le barre azzurre sono le piogge: quando la linea risale dopo la pioggia, i pannelli erano sporchi.{daily ? " Andamento giornaliero." : ""}</DialogDescription>
        </DialogHeader>
        {oggiPct != null && (
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-slate-50 p-3 text-center" data-testid="pulizia-storico-oggi">
              <b className="block text-3xl font-extrabold text-slate-800">{oggiPct}%</b>
              <span className="text-[11px] font-semibold text-slate-500">Pulizia oggi</span>
            </div>
            <div className="rounded-2xl bg-slate-50 p-3 text-center" data-testid="pulizia-storico-mese">
              <b className="block text-3xl font-extrabold text-slate-800">{mesePct != null ? `${mesePct}%` : "—"}</b>
              <span className="text-[11px] font-semibold text-slate-500">Pulizia del mese</span>
            </div>
          </div>
        )}
        {!pts ? <div className="flex h-56 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
          : validi.length < 4 ? <p className="py-10 text-center text-sm text-slate-500" data-testid="pulizia-storico-vuoto">Non ci sono ancora abbastanza giornate di sole per disegnare l'andamento.</p>
          : <div className="h-60" data-testid="pulizia-storico-grafico">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={pts} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey={xKey} tickFormatter={mese} interval={daily ? 30 : 8} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="p" domain={[(m) => Math.min(60, Math.floor(m / 10) * 10), 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} unit="%" />
                <YAxis yAxisId="r" orientation="right" hide domain={[0, (m) => Math.max(40, m * 3)]} />
                <Tooltip formatter={(v, k) => (k === "pioggia_mm" ? [`${String(v).replace(".", ",")} mm`, "Pioggia"] : [`${v}%`, "Pulizia"])} labelFormatter={(s) => (daily ? giorno(s) : `Settimana del ${giorno(s)}`)} />
                <Bar yAxisId="r" dataKey="pioggia_mm" fill="#7DD3FC" radius={[3, 3, 0, 0]} />
                <Line yAxisId="p" dataKey={lineKey} stroke="#10B981" strokeWidth={2.5} dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>}
        {d?.perche && <p className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="pulizia-storico-perche"><b className="text-slate-800">{d.titolo}.</b> {d.perche}</p>}
      </DialogContent>
    </Dialog>
  );
}
